import { Button, Checkbox, Col, Form, Modal, Row, Spin, Input, InputNumber } from 'antd';
import { useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import BreadCrumbs from '../../../../common/components/Breadcrumbs';
import { validateMessages } from '../../../../common/constants/common';
import { Page } from '../../../../common/constants/pageAction';
import { getObjectForUpdateMultiple } from '../../../../common/helperFunction';
import { IConfigSoftwareVersion } from '../../../../services/master/configSoftwareVersion/configSoftwareVersion.model';
import { useAppSelector, useAppDispatch } from '../../../../store/app.hooks';
import { updateMultiple } from '../../../../store/common/common.action';
import {
  clearMultipleUpdateMessages,
  commonSelector,
} from '../../../../store/common/common.reducer';
import {
  getConfigSoftwareVersionById,
  saveConfigSoftwareVersion,
} from '../../../../store/master/configSoftwareVersion/configSoftwareVersion.action';
import {
  clearConfigSoftwareVersionGetById,
  clearConfigSoftwareVersionMessages,
  configSoftwareVersionSelector,
} from '../../../../store/master/configSoftwareVersion/configSoftwareVersion.reducer';
import { IAddConfigSoftwareVersionProps } from './addConfigSoftwareVersion.model';
import _ from 'lodash';

const AddConfigSoftwareVersionModal: React.FC<IAddConfigSoftwareVersionProps> = (props) => {
  const configSoftwareVersion = useAppSelector(configSoftwareVersionSelector);
  const common = useAppSelector(commonSelector);
  const dispatch = useAppDispatch();
  const { id, showModal, handleModalClose, refreshDataTable, isMultiple, valuesForSelection } =
    props;

  const isNew: boolean = id || isMultiple ? false : true;
  const title = useMemo(() => {
    return (
      <>
        {isNew ? 'Add ' : 'Edit '} <BreadCrumbs pageName={Page.ConfigSoftwareVersion} level={1} />
      </>
    );
  }, [isNew]);
  const submitButtonText = useMemo(() => {
    return isNew ? 'Save' : 'Update';
  }, [isNew]);

  const [form] = Form.useForm();

  let initialValues: IConfigSoftwareVersion = {
    version: '',
    version_index: null,
  };

  const onFinish = (values: any) => {
    const inputValues: IConfigSoftwareVersion = {
      ...values,
      id: id ? +id : null,
    };
    if (!isMultiple) {
      dispatch(saveConfigSoftwareVersion(inputValues));
    } else {
      const result = getObjectForUpdateMultiple(
        valuesForSelection,
        inputValues,
        configSoftwareVersion.search.tableName
      );
      if (result) {
        dispatch(updateMultiple(result));
      }
    }
  };

  const fillValuesOnEdit = async (data: IConfigSoftwareVersion) => {
    if (data) {
      initialValues = {
        version: data.version,
        version_index: _.isNull(data.version_index) ? null : data.version_index,
      };
      form.setFieldsValue(initialValues);
    }
  };

  useEffect(() => {
    if (configSoftwareVersion.save.messages.length > 0) {
      if (configSoftwareVersion.save.hasErrors) {
        toast.error(configSoftwareVersion.save.messages.join(' '));
      } else {
        toast.success(configSoftwareVersion.save.messages.join(' '));
        handleModalClose();
        refreshDataTable();
      }
      dispatch(clearConfigSoftwareVersionMessages());
    }
  }, [configSoftwareVersion.save.messages]);

  useEffect(() => {
    if (common.save.messages.length > 0) {
      if (common.save.hasErrors) {
        toast.error(common.save.messages.join(' '));
      } else {
        toast.warn(common.save.messages.join(' '));
        handleModalClose();
        refreshDataTable();
      }
      dispatch(clearMultipleUpdateMessages());
    }
  }, [common.save.messages]);

  useEffect(() => {
    if (+id > 0 && configSoftwareVersion.getById.data) {
      const data = configSoftwareVersion.getById.data;
      fillValuesOnEdit(data);
    }
  }, [configSoftwareVersion.getById.data]);

  useEffect(() => {
    if (+id > 0) {
      dispatch(getConfigSoftwareVersionById(+id));
    }
    return () => {
      dispatch(clearConfigSoftwareVersionGetById());
    };
  }, [dispatch]);

  return (
    <>
      <Modal
        wrapClassName="custom-modal"
        title={title}
        centered
        visible={showModal}
        onCancel={handleModalClose}
        footer={false}
      >
        {configSoftwareVersion.getById.loading ? (
          <div className="spin-loader">
            <Spin spinning={configSoftwareVersion.getById.loading} />
          </div>
        ) : (
          <Form
            form={form}
            name="configSoftwareVersion"
            initialValues={initialValues}
            onFinish={onFinish}
            validateMessages={validateMessages}
          >
            <Row gutter={[30, 15]} className="form-label-hide">
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'version']} valuePropName="checked" noStyle>
                      <Checkbox>Version</Checkbox>
                    </Form.Item>
                  ) : (
                    'Version'
                  )}
                  <Form.Item
                    name="version"
                    label="Version"
                    className="m-0"
                    rules={[{ required: !isMultiple, max: 255 }]}
                  >
                    <Input className="form-control" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div className="form-group m-0">
                  {isMultiple ? (
                    <Form.Item name={['checked', 'version_index']} valuePropName="checked" noStyle>
                      <Checkbox>Version Index</Checkbox>
                    </Form.Item>
                  ) : (
                    'Version Index'
                  )}
                  <Form.Item
                    name="version_index"
                    label="Version Index"
                    className="m-0"
                    rules={[{ type: 'integer' }]}
                  >
                    <InputNumber className="form-control w-100" />
                  </Form.Item>
                </div>
              </Col>
            </Row>
            <div className="btns-block modal-footer">
              <Button
                key="submit"
                type="primary"
                htmlType="submit"
                loading={configSoftwareVersion.save.loading || common.save.loading}
              >
                {submitButtonText}
              </Button>
              <Button key="back" onClick={handleModalClose}>
                Cancel
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    </>
  );
};
export default AddConfigSoftwareVersionModal;
